import {
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class ResendEmailRPC implements
  LeoRPC<ResendEmailRPC.Request, ResendEmailRPC.Response, ResendEmailRPC.Errors.Errors>
{
  abstract execute(
    request: ResendEmailRPC.Request
  ): Promise<LeoRPCResult<ResendEmailRPC.Response, ResendEmailRPC.Errors.Errors>>;
}

export namespace ResendEmailRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    emailId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isResendEmailRPCRequest: boolean = true;
  
    readonly emailId: LeoUUID;
  
  
    constructor(
    emailId: LeoUUID
    ) {
      this.emailId = emailId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const emailId = getLeoUuid(dto, "emailId", InvalidRequestEmailIdError);
      return new Request(
        emailId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          emailId: this.emailId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ResendEmailRPC(emailId='${this.emailId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ResendEmailRPC.Response()`;
  };

  export enum RPCError {
    InvalidEmailId = "INVALID_EMAIL_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    ResendNotAllowed = "RESEND_NOT_ALLOWED",
    UnexpectedError = "UNEXPECTED_ERROR"
  }

  export namespace Errors {
    
    export interface InvalidEmailIdDTO {
      code: string;
    }
    
    export class InvalidEmailId extends LeoRPCError<RPCError> {
      readonly isInvalidEmailId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidEmailId;
        super(code);
      }
    
    
      toDTO(): InvalidEmailIdDTO {
        const dto: InvalidEmailIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidEmailId.prototype.toString = function toString(): string {
      return `ResendEmailRPC.INVALID_EMAIL_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ResendEmailRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface ResendNotAllowedDTO {
      code: string;
    }
    
    export class ResendNotAllowed extends LeoRPCError<RPCError> {
      readonly isResendNotAllowed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ResendNotAllowed;
        super(code);
      }
    
    
      toDTO(): ResendNotAllowedDTO {
        const dto: ResendNotAllowedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ResendNotAllowed.prototype.toString = function toString(): string {
      return `ResendEmailRPC.RESEND_NOT_ALLOWED()`;
    };
    
    
    export interface UnexpectedErrorDTO {
      code: string;
    }
    
    export class UnexpectedError extends LeoRPCError<RPCError> {
      readonly isUnexpectedError: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnexpectedError;
        super(code);
      }
    
    
      toDTO(): UnexpectedErrorDTO {
        const dto: UnexpectedErrorDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnexpectedError.prototype.toString = function toString(): string {
      return `ResendEmailRPC.UNEXPECTED_ERROR()`;
    };
    

    export type Errors = InvalidEmailId | ProjectAlreadyArchived | ResendNotAllowed | UnexpectedError;
    export type ErrorsDTO = InvalidEmailIdDTO | ProjectAlreadyArchivedDTO | ResendNotAllowedDTO | UnexpectedErrorDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidEmailId:
          return new InvalidEmailId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.ResendNotAllowed:
          return new ResendNotAllowed();
        case RPCError.UnexpectedError:
          return new UnexpectedError();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestEmailIdError extends ResendEmailRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailIdError";
  }
}


export class InvalidResponseInvalidEmailIdError extends ResendEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidEmailIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ResendEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseResendNotAllowedError extends ResendEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseResendNotAllowedError";
  }
}

export class InvalidResponseUnexpectedErrorError extends ResendEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnexpectedErrorError";
  }
}

